import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Grid,
  TableSortLabel,
  useTheme,
  useMediaQuery,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Search, Inventory, CalendarToday, AccountBalance, Payment, CurrencyExchange } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getOrders, getProcessorOrders } from "../../../api/orders/orders";
import { getExternalProcessorsAPI } from "../../../api/orders/orders";
import { MenuButton } from "../../../components/Menu";

interface ProcessorTransaction {
  id: string;
  order_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_price: number;
  payable_amount: number;
  amount_paid: number;
  balance_due: number;
  payment_status: number;
  order_date: string;
  processor_type: string;
  external_processor: string;
  product_id_type: string;
  product_id_single: string;
  rmbValue: string;
  commission: number;
  costPrice: number;
  sellingPrice: number;
  costRate: number;
  sellingRate: number;
  cryptoAmount: number;
  cryptoType: string;
  active_status: string;
  username: string;
  processor_category: string;
}

interface ProcessorSummary {
  processorType: string;
  externalType: string;
  totalTransactions: number;
  totalSales: number;
  totalCommission: number;
  totalAmountPaid: number;
  averageTransaction: number;
}

interface ExternalProcessor {
  id: string;
  name: string;
  type: string;
  category: string;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof ProcessorTransaction;
  label: string;
  numeric: boolean;
  sortable: boolean;
  width?: string;
}

const headCells: HeadCell[] = [
  { id: 'order_id', label: 'Order ID', numeric: false, sortable: true, width: '15%' },
  { id: 'customer_name', label: 'Customer', numeric: false, sortable: true, width: '20%' },
  { id: 'order_date', label: 'Date', numeric: false, sortable: true, width: '15%' },
  { id: 'processor_category', label: 'Processor Name', numeric: false, sortable: true, width: '15%' },
  { id: 'processor_type', label: 'Processor Type', numeric: false, sortable: true, width: '15%' }, 
  { id: 'amount_paid', label: 'Amount Paid (₦)', numeric: true, sortable: true, width: '15%' },
  { id: 'commission', label: 'Commission (₦)', numeric: true, sortable: true, width: '15%' },
];

const ProcessorPerformancePage: React.FC = () => {
  const [transactions, setTransactions] = useState<ProcessorTransaction[]>([]);
  const [filteredData, setFilteredData] = useState<ProcessorTransaction[]>([]);
  const [processorSummary, setProcessorSummary] = useState<ProcessorSummary[]>([]);
  const [externalProcessors, setExternalProcessors] = useState<ExternalProcessor[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof ProcessorTransaction>('order_date');
  const [selectedProcessor, setSelectedProcessor] = useState<string>('all');
  const [selectedExternalProcessor, setSelectedExternalProcessor] = useState<string>('all');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Function to safely convert to number
  const safeNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Fetch external processors
  const fetchExternalProcessors = async () => {
    try {
      const response = await getExternalProcessorsAPI();
      console.log("External processors response:", response);
      
      if (response.data && Array.isArray(response.data)) {
        setExternalProcessors(response.data);
      } else if (Array.isArray(response)) {
        setExternalProcessors(response);
      } else {
        console.warn("Unexpected response format from getExternalProcessorsAPI:", response);
        setExternalProcessors([]);
      }
    } catch (error) {
      console.error("Error fetching external processors:", error);
      setExternalProcessors([]);
    }
  };

  const fetchProcessorTransactions = async () => {
    setStatus({ loading: true, error: false });
    try {
      // Remove date params from API call since we'll filter on frontend
      const response = await getProcessorOrders();
      const ordersData = response.data || [];
      
      // Process the data to ensure numeric fields are properly converted
      const processedData = ordersData.map((order: any) => ({
        ...order,
        total_price: safeNumber(order.total_price),
        payable_amount: safeNumber(order.payable_amount),
        amount_paid: safeNumber(order.amount_paid),
        balance_due: safeNumber(order.balance_due),
        commission: safeNumber(order.commission),
        costPrice: safeNumber(order.costPrice),
        sellingPrice: safeNumber(order.sellingPrice),
        costRate: safeNumber(order.costRate),
        sellingRate: safeNumber(order.sellingRate),
        cryptoAmount: safeNumber(order.cryptoAmount),
      }));
      
      setTransactions(processedData);
    } catch (error) {
      console.error("Error fetching processor transactions:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setStatus({ loading: false, error: false });
    }
  };

  const calculateProcessorSummary = (data: ProcessorTransaction[]) => {
    const summaryMap = new Map();
    
    data.forEach(transaction => {
      const key = `${transaction.processor_type}_${transaction.external_processor || 'NONE'}`;
      const existing = summaryMap.get(key) || {
        processorType: transaction.processor_type,
        externalType: transaction.external_processor || 'NONE',
        totalTransactions: 0,
        totalSales: 0,
        totalCommission: 0,
        totalAmountPaid: 0,
      };
      
      existing.totalTransactions += 1;
      existing.totalSales += safeNumber(transaction.amount_paid);
      existing.totalCommission += safeNumber(transaction.commission);
      existing.totalAmountPaid += safeNumber(transaction.amount_paid);
      
      summaryMap.set(key, existing);
    });

    // Calculate averages and format the summary
    const summary = Array.from(summaryMap.values()).map(item => ({
      ...item,
      averageTransaction: item.totalTransactions > 0 ? item.totalSales / item.totalTransactions : 0,
    }));

    setProcessorSummary(summary);
  };

  useEffect(() => {
    fetchProcessorTransactions();
    fetchExternalProcessors();
  }, []);

  // Filter data based on all criteria
  useEffect(() => {
    let filtered = transactions;

    // Apply date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(transaction => {
        const transactionDate = new Date(transaction.order_date);
        
        if (dateRange.start && dateRange.end) {
          // Both start and end dates selected - filter between dates (inclusive)
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          
          return transactionDate >= startOfDay && transactionDate <= endOfDay;
        } else if (dateRange.start) {
          // Only start date selected - filter from start date onwards
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          return transactionDate >= startOfDay;
        } else if (dateRange.end) {
          // Only end date selected - filter up to end date
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          return transactionDate <= endOfDay;
        }
        
        return true;
      });
    }

    // Apply processor type filter
    if (selectedProcessor !== 'all') {
      if (selectedProcessor === 'INTERNAL') {
        filtered = filtered.filter(transaction => transaction.processor_type === 'INTERNAL');
      } else if (selectedProcessor === 'EXTERNAL_USD') {
        filtered = filtered.filter(transaction => 
          transaction.processor_type === 'EXTERNAL' && transaction.processor_category === 'USD'
        );
      } else if (selectedProcessor === 'EXTERNAL_CHINESE') {
        filtered = filtered.filter(transaction => 
          transaction.processor_type === 'EXTERNAL' && transaction.processor_category === 'CHINESE'
        );
      }
    }

    // Apply external processor filter
    if (selectedExternalProcessor !== 'all') {
      filtered = filtered.filter(transaction => 
        transaction.external_processor === selectedExternalProcessor
      );
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((transaction) => {
        const customerName = (transaction.customer_name || "").toLowerCase();
        const customerEmail = (transaction.customer_email || "").toLowerCase();
        const orderId = (transaction.order_id || "").toLowerCase();
        const query = searchQuery.toLowerCase();

        return (
          customerName.includes(query) ||
          customerEmail.includes(query) ||
          orderId.includes(query)
        );
      });
    }

    setFilteredData(filtered);
    calculateProcessorSummary(filtered);
    setPage(0); // Reset to first page when filters change
  }, [searchQuery, selectedProcessor, selectedExternalProcessor, transactions, dateRange]);

  const formatDateForDisplay = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleDateRangeChange = (field: 'start' | 'end') => (date: Date | null) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleProcessorFilterChange = (processor: string) => {
    setSelectedProcessor(processor);
    setSelectedExternalProcessor('all');
  };

  const handleExternalProcessorChange = (event: any) => {
    setSelectedExternalProcessor(event.target.value);
  };

  const handleRequestSort = (property: keyof ProcessorTransaction) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  // Get processor type color
  const getProcessorColor = (processorType: string, processor_category: string) => {
    if (processorType === 'INTERNAL') return 'primary';
    if (processorType === 'EXTERNAL' && processor_category === 'USD') return 'success';
    if (processorType === 'EXTERNAL' && processor_category === 'CHINESE') return 'warning';
    return 'default';
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    return filteredData.sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, order, orderBy]);

  // Calculate table totals for current page
  const tableTotals = {
    totalAmountPaid: filteredData.reduce((sum, transaction) => sum + safeNumber(transaction.amount_paid), 0),
    totalCommission: filteredData.reduce((sum, transaction) => sum + safeNumber(transaction.commission), 0),
  };

  const getInitials = (name: string) => {
    return name ? name.charAt(0).toUpperCase() : 'C';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>  <MenuButton/>
            <IonButtons slot="start">
              {/* <IonMenuButton /> */}
            </IonButtons>
            <IonTitle>Processor Report</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <IonLoading
            isOpen={status.loading}
            message={"Fetching processor performance..."}
            spinner="circles"
          />

          <Box mb={3}>
            <Typography variant="h5" fontWeight="600" gutterBottom>
              Processor Metrics Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor and analyze processor transaction metrics
            </Typography>
          </Box>

          {/* Processor Type Filter Chips */}
          <Box mb={3}>
            <Typography variant="body2" fontWeight="500" gutterBottom>
              Filter by Processor Type:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip
                label="All Processors"
                clickable
                color={selectedProcessor === 'all' ? 'primary' : 'default'}
                onClick={() => handleProcessorFilterChange('all')}
              />
              <Chip
                label="Internal Processor"
                clickable
                color={selectedProcessor === 'INTERNAL' ? 'primary' : 'default'}
                onClick={() => handleProcessorFilterChange('INTERNAL')}
              />
              <Chip
                label="External USD"
                clickable
                color={selectedProcessor === 'EXTERNAL_USD' ? 'success' : 'default'}
                onClick={() => handleProcessorFilterChange('EXTERNAL_USD')}
              />
              <Chip
                label="External Chinese"
                clickable
                color={selectedProcessor === 'EXTERNAL_CHINESE' ? 'warning' : 'default'}
                onClick={() => handleProcessorFilterChange('EXTERNAL_CHINESE')}
              />
            </Box>

            {/* External Processor Dropdown Filter */}
            {(selectedProcessor === 'EXTERNAL_USD' || selectedProcessor === 'EXTERNAL_CHINESE' || selectedProcessor === 'all') && (
              <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Processor</InputLabel>
                  <Select
                    value={selectedExternalProcessor}
                    label="Filter by Processor"
                    onChange={handleExternalProcessorChange}
                  >
                    <MenuItem value="all">All Processors</MenuItem>
                    {externalProcessors.map((processor) => (
                      <MenuItem key={processor.id} value={processor.name}>
                        {processor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          <Card sx={{ width: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
                mb={2}
                flexDirection={isMobile ? "column" : "row"}
                gap={2}
              >
                <Typography variant="h6" fontWeight="500" mb={isMobile ? 1 : 0}>
                  Processor Transactions ({filteredData.length})
                </Typography>
                
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent={isMobile ? "flex-start" : "flex-end"}>
                  {/* Date Range Filter */}
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <CalendarToday sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <DatePicker
                      label="Start Date"
                      value={dateRange.start}
                      onChange={handleDateRangeChange('start')}
                      slotProps={{ 
                        textField: { 
                          size: 'small', 
                          sx: { width: isMobile ? 130 : 150 } 
                        } 
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      to
                    </Typography>
                    <DatePicker
                      label="End Date"
                      value={dateRange.end}
                      onChange={handleDateRangeChange('end')}
                      slotProps={{ 
                        textField: { 
                          size: 'small', 
                          sx: { width: isMobile ? 130 : 150 } 
                        } 
                      }}
                    />
                  </Box>

                  {/* Search Field */}
                  <TextField
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: isMobile ? '100%' : 200 }}
                    size="small"
                  />
                </Box>
              </Box>

              <Paper sx={{ width: '100%', overflow: 'auto' }}>
                <Table sx={{ 
                  minWidth: isMobile ? 800 : '100%',
                  tableLayout: 'fixed'
                }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          sx={{ 
                            fontWeight: 600,
                            width: headCell.width,
                            px: 1,
                            py: 1.5
                          }}
                          align={headCell.numeric ? 'right' : 'left'}
                          sortDirection={orderBy === headCell.id ? order : false}
                        >
                          {headCell.sortable ? (
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={orderBy === headCell.id ? order : 'asc'}
                              onClick={() => handleRequestSort(headCell.id)}
                            >
                              <Typography variant="body2" fontWeight="600">
                                {headCell.label}
                              </Typography>
                            </TableSortLabel>
                          ) : (
                            <Typography variant="body2" fontWeight="600">
                              {headCell.label}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((transaction) => (
                        <TableRow
                          key={transaction.id}
                          sx={{ 
                            "&:hover": { backgroundColor: "#f5f7fa" },
                            '& td': { px: 1, py: 1.5 }
                          }}
                        >
                          <TableCell sx={{ width: '15%' }}>
                            <Typography variant="body2" fontWeight="500" noWrap>
                              {transaction.order_id || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '20%' }}>
                            <Box display="flex" alignItems="center">
                              <Avatar 
                                sx={{ 
                                  bgcolor: "#e3f2fd", 
                                  width: 28, 
                                  height: 28, 
                                  mr: 1.5,
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {getInitials(transaction.customer_name)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="500" noWrap>
                                  {transaction.customer_name || "N/A"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {transaction.customer_phone || "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '15%' }}>
                            <Typography variant="body2" noWrap>
                              {formatDateForDisplay(transaction.order_date)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ width: '15%' }}>
                            <Typography variant="body2" noWrap>
                              {transaction.external_processor || "N/A"}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ width: '15%' }}>
                            <Chip 
                              label={transaction.processor_category || "N/A"} 
                              size="small" 
                              color={getProcessorColor(transaction.processor_type, transaction.processor_category) as any}
                            />
                          </TableCell>

                          <TableCell align="right" sx={{ width: '15%' }}>
                            <Typography variant="body2" fontWeight="500" color="success.main" noWrap>
                              {formatCurrency(transaction.amount_paid || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ width: '15%' }}>
                            <Typography variant="body2" fontWeight="500" color="primary.main" noWrap>
                              {formatCurrency(transaction.commission || 0)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    
                    {/* Totals Row */}
                    {sortedData.length > 0 && (
                      <TableRow sx={{ 
                        backgroundColor: '#f8f9fa', 
                        borderTop: '2px solid #dee2e6',
                        '& td': { px: 1, py: 1.5 }
                      }}>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" fontWeight="bold">
                            Totals
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.main" noWrap>
                            {formatCurrency(tableTotals.totalAmountPaid)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary.main" noWrap>
                            {formatCurrency(tableTotals.totalCommission)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {filteredData.length === 0 && !status.loading && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="textSecondary">
                      No processor transaction data found.
                    </Typography>
                  </Box>
                )}
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
              </Paper>
            </CardContent>
          </Card>
        </IonContent>
      </IonPage>
    </LocalizationProvider>
  );
};

export default ProcessorPerformancePage;